import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  fetchCouriers,
  fetchStaffCouriers,
  fetchTemplates,
  createCourierThunk,
  updateCourierThunk,
  deleteCourierThunk,
} from './couriersThunks';

const initialState = {
  couriers: [],
  templates: {},
  totalCount: 0,
  loading: false,
  error: null,
  filters: {
    page: 1,
    rowsPerPage: 20,
    search: '',
    orderBy: 'name',
    order: 'asc'
  }
};

const couriersSlice = createSlice({
  name: 'couriers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setSorting: (state, action) => {
      state.filters.orderBy = action.payload.orderBy;
      state.filters.order = action.payload.order;
      state.filters.page = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Couriers
      .addCase(fetchCouriers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCouriers.fulfilled, (state, action) => {
        state.loading = false;
        state.couriers = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchCouriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // The same table read as the internal team, into the same three fields:
      // a session is either staff or it is not, so they never compete.
      .addCase(fetchStaffCouriers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffCouriers.fulfilled, (state, action) => {
        state.loading = false;
        state.couriers = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchStaffCouriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Templates
      .addCase(fetchTemplates.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Create
      .addCase(createCourierThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCourierThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCourierThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateCourierThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCourierThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCourierThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCourierThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCourierThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteCourierThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, setPage, setRowsPerPage, setSearchFilter, setSorting } = couriersSlice.actions;

const persistConfig = {
  key: 'adminCouriersFilter',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, couriersSlice.reducer);
